import clsx from 'clsx';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import * as React from 'react';

import { ScreenTypeContext } from '@gitbook/spine-react';

import { SidePanelExpandableContext } from './context';

export interface SidePanelItem {
    /** Content of the side-panel to render */
    children: React.ReactNode;
}

export interface SidePanelProps {
    /** Main content always displayed. */
    children: React.ReactNode;
    /** Side-content to render, null if it should be closed. */
    sidePanel: SidePanelItem | null;
    /** Key to ensure AnimatePresence can clean up across screen navigations. */
    animationKey: string;
}

const SIDE_PANEL_WIDTH = 350;
const SIDE_PANEL_EXPANDED_WIDTH = 550;
const SIDE_PANEL_PADDING = 6;

/**
 * Component to render a main body with a side-panel on the right.
 */
export function SidePanel(props: SidePanelProps): React.ReactElement {
    const { children, sidePanel, animationKey } = props;
    const [expanded, setExpanded] = React.useState(false);
    const shouldReduceMotion = useReducedMotion();

    const screenType = React.useContext(ScreenTypeContext);
    const isMobile = screenType === 'mobile';

    const hasSidePanel = !!sidePanel;

    const expandableContext = React.useMemo(() => ({ expanded, setExpanded }), [expanded]);

    React.useEffect(() => {
        if (!hasSidePanel) {
            setExpanded(false);
        }
    }, [hasSidePanel]);

    /**
     * The sidepanel transition is two-fold:
     *   1. The main content's children animates a margin-right equal to the current width of the sidepanel.
     *   2. The sidepanel itself animates with either:
     *      - `x` for opening/closing (to avoid layout reflows we dont want to animate from width: 0px)
     *      - `width` when toggling between expanded (550px) and normal (350px).
     *
     *   🚩 Be careful with layout changes here. The transition may look simple, but it requires having the right combination of margins, paddings, and widths.
     */
    const spring = {
        type: 'spring',
        stiffness: 127,
        damping: 21,
    };
    const transition = shouldReduceMotion ? { duration: 0 } : spring;

    const sidePanelWidth = expanded ? SIDE_PANEL_EXPANDED_WIDTH : SIDE_PANEL_WIDTH;

    return (
        <SidePanelExpandableContext.Provider value={expandableContext}>
            <motion.div
                key={`app-content-${animationKey}`}
                className="flex min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-side"
                animate={{
                    marginRight:
                        sidePanel && !isMobile
                            ? expanded
                                ? SIDE_PANEL_EXPANDED_WIDTH + SIDE_PANEL_PADDING
                                : SIDE_PANEL_WIDTH + SIDE_PANEL_PADDING
                            : 0,
                }}
                initial={false}
                transition={transition}
            >
                <div
                    className="app-framing-styles relative flex flex-1 flex-col"
                    // Allow the parent AppLayout to detect whether a child screen is rendering with sidepanels, to skip framing styles. See app.css for more.
                    data-side-panel-frame
                >
                    {children}
                </div>
                <AnimatePresence key={animationKey} initial={false}>
                    {sidePanel ? (
                        <motion.div
                            key={`sidepanel-${animationKey}`}
                            className={clsx(
                                'absolute top-1.5 right-1.5 bottom-1.5 z-10 flex flex-col overflow-hidden rounded-xl border-0.5 border-control bg-base shadow-subtle md:fixed'
                            )}
                            transition={transition}
                            variants={{
                                exit: {
                                    x: sidePanelWidth + SIDE_PANEL_PADDING,
                                    width: sidePanelWidth,
                                },
                                animate: {
                                    x: 0,
                                    width: sidePanelWidth,
                                },
                            }}
                            initial="exit"
                            animate="animate"
                            exit="exit"
                        >
                            {sidePanel.children}
                        </motion.div>
                    ) : null}
                </AnimatePresence>
            </motion.div>
        </SidePanelExpandableContext.Provider>
    );
}

export type SidePanelBodyProps = React.ComponentPropsWithoutRef<'div'>;

export function SidePanelBody(props: SidePanelBodyProps) {
    return <div {...props} className={clsx('p-6', props.className)} />;
}
